"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer({
  activeSection
}: {
  activeSection: string;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => {
        const isActive = item.href && (item.href === activeSection || item.href.endsWith(activeSection));
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild active={isActive}>
                <Link to={item.href || "#"} className={cn("group relative inline-flex h-10 items-center justify-center px-4 py-2 text-base font-medium transition-colors focus:outline-none", isActive ? "text-[#0A1628]" : "text-[#4B5563] hover:text-[#0A1628] focus:text-[#0A1628]")}>
                  {item.label}
                  <span className={cn("absolute bottom-1 left-4 right-4 h-[2px] origin-left transition-transform duration-300 ease-out", isActive ? "scale-x-100 bg-[#4F46E5]" : "scale-x-0 bg-[#0A1628] group-hover:scale-x-100")} />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  setOpen,
  activeSection
}: {
  setOpen: (open: boolean) => void;
  activeSection: string;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-4 mt-8">
      {menuItems.map((item, i) => {
      const isActive = item.href && (item.href === activeSection || item.href.endsWith(activeSection));
      return <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} className={cn("block py-3 text-lg font-medium transition-colors", isActive ? "text-[#4F46E5]" : "text-[#4B5563] hover:text-[#0A1628]")} onClick={() => setOpen(false)}>
              {item.label}
            </Link>
          </div>;
    })}
      <div className="mt-6 pt-6 border-t border-border">
        <Button asChild className="w-full bg-[#0A1628] text-white hover:bg-[#0A1628]/90 rounded-[6px] h-12 text-base">
          <Link to="https://calendly.com/oscar-martinez-fontenla/30min" onClick={() => setOpen(false)}>
            Beratung buchen
          </Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(`#${entry.target.id}`);
        }
      });
    }, {
      threshold: 0.5
    });
    document.querySelectorAll("section[id]").forEach(section => {
      observer.observe(section);
    });
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-white/80 backdrop-blur-md border-b border-[#E5E7EB]" : "bg-white border-b border-transparent")}>
      <div className="container mx-auto px-4 min-h-[80px] flex items-center justify-between">
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="18">
            <DesktopMenuConsumer activeSection={activeSection} />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center flex-shrink-0">
          <Button asChild className="bg-[#0A1628] text-white hover:bg-[#0A1628]/90 rounded-[6px] px-6">
            <Link to="https://calendly.com/oscar-martinez-fontenla/30min">
              Beratung buchen
            </Link>
          </Button>
        </div>

        <div className="lg:hidden flex items-center">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-[#0A1628] hover:bg-muted">
                <Menu className="size-6" />
                <span className="sr-only">Toggle mobile menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-white border-l border-[#E5E7EB]">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex items-center justify-between mt-2">
                <WvcLogo className="h-7 w-auto" />
              </div>
              <MenuProvider menu_id="18">
                <MobileMenuConsumer setOpen={setMobileMenuOpen} activeSection={activeSection} />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}